import { useReactFlow } from "@xyflow/react";
import { useBuild } from "app/components/build/Show/lib/BuildContext";
import { Build } from "app/components/build/Show/lib/types";
import { BuildStates } from "app/constants/BuildStates";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { State } from "app/lib/pipeline";
import { ControlPanel, ControlButton } from "./ControlButton";
import Icon from "app/components/shared/Icon";
import { useTrack } from "app/components/build/Show/lib/useTrack";
import { useHotKey } from "./useHotKey";
import FollowBorder from "./FollowBorder";

import elevatorMusic from "app/sounds/elevator-music.mp3";
// MP3 sourced from: https://pixabay.com/music/elevator-music-elevator-music-bossa-nova-background-music-version-60s-10900/
// License: https://pixabay.com/service/license-summary/

const RUNNING_STATES: BuildStates[] = ["failing", "running", "started"];
function isBuildRunning(build: Build) {
  return RUNNING_STATES.includes(build.state);
}

export default function NavigationControls() {
  const { build } = useBuild();
  const { fitView } = useReactFlow();
  const track = useTrack();

  const nodes = useMemo(() => {
    return build.steps
      .filter((step) => step.state === State.Failing || step.state === State.Running)
      .map((step) => ({ id: step.uuid }));
  }, [build.steps]);

  const [follow, setFollow] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const toggleFollow = useCallback(() => {
    setFollow(!follow);
    track("Enabled Follow Mode");
  }, [follow]);

  const setFollowFalse = useCallback(() => {
    setFollow(false);
    track("Enabled Hand Mode");
  }, []);

  useEffect(() => {
    if (follow && nodes?.length) {
      fitView({
        nodes,
        duration: 450,
      });
    }
  }, [fitView, nodes, follow]);

  // Initialize audio instance once and store it in ref
  useEffect(() => {
    audioRef.current = new Audio(elevatorMusic);
    audioRef.current.loop = true;
    audioRef.current.volume = 0.2;

    return () => {
      audioRef.current?.pause();
    };
  }, [elevatorMusic]);

  const handleMuteToggle = useCallback(() => {
    setIsMuted((prevIsMuted) => {
      if (audioRef.current) {
        audioRef.current.muted = !prevIsMuted;
        if (!prevIsMuted) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        } else {
          audioRef.current.volume = 0.2;
          audioRef.current.play().catch((error) => console.error("Error playing the audio", error));
        }
      }
      return !prevIsMuted;
    });
  }, []);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = isMuted;
      if (!follow || !isBuildRunning(build)) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      // if audio was previously unmuted, start playing it again
      if (!isMuted && follow && isBuildRunning(build)) {
        audioRef.current.play().catch((error) => console.error("Error playing the audio", error));
      }
    }
  }, [isMuted, follow, build.state]);

  useEffect(() => {
    if (!isBuildRunning(build) && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsMuted(true);
    }
  }, [build.state]);

  useHotKey("j", toggleFollow);
  useHotKey("Escape", setFollowFalse);

  return (
    <div className="absolute h-full w-full bottom-0 left-0 pointer-events-none">
      <FollowBorder toggled={follow} onClick={toggleFollow} onMuteToggle={handleMuteToggle} />

      <div className="absolute bottom-3 left-3 pointer-events-auto">
        <ControlPanel>
          <ControlButton onClick={toggleFollow} label="Follow mode (J)" toggled={follow}>
            <Icon icon="custom/outline/follow-mode" className="h-[18px] mt-[-3px] mr-1 w-5" />
            Follow mode
          </ControlButton>
        </ControlPanel>
      </div>
    </div>
  );
}
